import type { FormSchema } from "@/lib/database";
import { emailCopy } from "../../../emails/copy";
import { formCopy } from "./form-copy";

export const DEFAULT_RATE_LIMIT_SETTINGS = {
	enabled: true,
	maxSubmissions: 5,
	timeWindow: 30,
	blockDuration: 60,
	message: formCopy.rateLimitMessage,
};

export const DEFAULT_PROFANITY_FILTER_SETTINGS = {
	enabled: true,
	strictMode: true,
	replaceWithAsterisks: false,
	customWords: [],
	customMessage: formCopy.profanityMessage,
	whitelistedWords: [],
};

export const DEFAULT_RESPONSE_LIMIT_SETTINGS = {
	enabled: false,
	maxResponses: 100,
	message: formCopy.responseLimitMessage,
};

export const DEFAULT_METADATA_SETTINGS = {
	title: "",
	description: "",
	keywords: "",
	author: "",
	robots: "noindex" as const,
	canonicalUrl: "",
	ogTitle: "",
	ogDescription: "",
	ogImage: "",
	ogType: "website",
	twitterCard: "summary" as const,
	twitterTitle: "",
	twitterDescription: "",
	twitterImage: "",
	twitterSite: "",
	twitterCreator: "",
	noIndex: true,
	noFollow: false,
	noArchive: false,
	noSnippet: false,
	noImageIndex: false,
	noTranslate: false,
};

export const DEFAULT_PASSWORD_PROTECTION_SETTINGS = {
	enabled: false,
	password: "",
	message: formCopy.passwordMessage,
};

export const DEFAULT_DUPLICATE_PREVENTION_SETTINGS = {
	enabled: false,
	strategy: "combined" as const,
	mode: "one-time" as const,
	timeWindow: 1440,
	message: formCopy.duplicateMessage,
	allowOverride: false,
	maxAttempts: 1,
};

export const DEFAULT_BOT_PROTECTION_SETTINGS = {
	enabled: false,
	message: formCopy.botMessage,
};

export const DEFAULT_SOCIAL_MEDIA_SETTINGS = {
	enabled: true,
	platforms: {},
	showIcons: true,
	iconSize: "md" as const,
	position: "footer" as const,
};

export const DEFAULT_EMAIL_VALIDATION_SETTINGS = {
	allowedDomains: [],
	blockedDomains: [],
	autoCompleteDomain: "",
	requireBusinessEmail: false,
	customValidationMessage: "",
};

export const DEFAULT_NOTIFICATION_SETTINGS = {
	enabled: true,
	email: "",
	subject: emailCopy.formNotification.defaultSubject,
	message: emailCopy.formNotification.defaultMessage,
};

export const DEFAULT_LAYOUT_SETTINGS = {
	margin: "md" as const,
	padding: "lg" as const,
	maxWidth: "md" as const,
	borderRadius: "md" as const,
	spacing: "normal" as const,
	alignment: "left" as const,
};

export const DEFAULT_BEHAVIOR_SETTINGS = {
	autoFocusFirstField: false,
};

export const DEFAULT_COLOR_SETTINGS = {
	text: "#1f2937",
	border: "#e5e7eb",
	primary: "#3b82f6",
	background: "transparent",
};

export const DEFAULT_TYPOGRAPHY_SETTINGS = {
	fontSize: "base" as const,
	fontFamily: "Inter",
	fontWeight: "normal" as const,
	lineHeight: "normal" as const,
	letterSpacing: "normal" as const,
};

export function ensureDefaultFormSettings(schema: FormSchema): FormSchema {
	return {
		...schema,
		settings: {
			title: schema.settings?.title || formCopy.untitledForm,
			publicTitle: schema.settings?.publicTitle || "",
			description: schema.settings?.description || "",
			submitText: schema.settings?.submitText || formCopy.submitText,
			successMessage:
				schema.settings?.successMessage || formCopy.successMessage,
			redirectUrl: schema.settings?.redirectUrl || "",
			multiStep: schema.settings?.multiStep,
			showProgress: schema.settings?.showProgress !== false,
			hideHeader: schema.settings?.hideHeader,
			colors: {
				...DEFAULT_COLOR_SETTINGS,
				...schema.settings?.colors,
			},
			typography: {
				...DEFAULT_TYPOGRAPHY_SETTINGS,
				...schema.settings?.typography,
			},
			branding: {
				...schema.settings?.branding,
				socialMedia: {
					...DEFAULT_SOCIAL_MEDIA_SETTINGS,
					...schema.settings?.branding?.socialMedia,
				},
			},
			layout: {
				...DEFAULT_LAYOUT_SETTINGS,
				...schema.settings?.layout,
			},
			behavior: {
				...DEFAULT_BEHAVIOR_SETTINGS,
				...schema.settings?.behavior,
			},
			rateLimit: {
				...DEFAULT_RATE_LIMIT_SETTINGS,
				...schema.settings?.rateLimit,
			},
			profanityFilter: {
				...DEFAULT_PROFANITY_FILTER_SETTINGS,
				...schema.settings?.profanityFilter,
			},
			responseLimit: {
				...DEFAULT_RESPONSE_LIMIT_SETTINGS,
				...schema.settings?.responseLimit,
			},
			passwordProtection: {
				...DEFAULT_PASSWORD_PROTECTION_SETTINGS,
				...schema.settings?.passwordProtection,
			},
			notifications: {
				...DEFAULT_NOTIFICATION_SETTINGS,
				...schema.settings?.notifications,
			},
			duplicatePrevention: {
				...DEFAULT_DUPLICATE_PREVENTION_SETTINGS,
				...schema.settings?.duplicatePrevention,
			},
			botProtection: {
				...DEFAULT_BOT_PROTECTION_SETTINGS,
				...schema.settings?.botProtection,
			},
			api: {
				enabled: false,
				allowExternalSubmissions: false,
				...schema.settings?.api,
			},
			metadata: {
				...DEFAULT_METADATA_SETTINGS,
				...schema.settings?.metadata,
			},
		},
	};
}

export function ensureDefaultRateLimitSettings(schema: FormSchema): FormSchema {
	return ensureDefaultFormSettings(schema);
}

export function createDefaultFormSchema(options: {
	title?: string;
	publicTitle?: string;
	description?: string;
	multiStep?: boolean;
}): FormSchema {
	return {
		blocks: options.multiStep
			? [
					{
						id: "step-1",
						title: formCopy.firstStepTitle,
						description: formCopy.firstStepDescription,
						fields: [],
					},
				]
			: [
					{
						id: "default",
						title: "Form Fields",
						description: "",
						fields: [],
					},
				],
		fields: [],
		settings: {
			title: options.title || formCopy.untitledForm,
			publicTitle: options.publicTitle || "",
			description: options.description || "",
			submitText: formCopy.submitText,
			successMessage: formCopy.successMessage,
			redirectUrl: "",
			multiStep: options.multiStep,
			showProgress: options.multiStep !== false,
			hideHeader: false,
			colors: { ...DEFAULT_COLOR_SETTINGS },
			typography: { ...DEFAULT_TYPOGRAPHY_SETTINGS },
			branding: {
				socialMedia: { ...DEFAULT_SOCIAL_MEDIA_SETTINGS },
			},
			layout: { ...DEFAULT_LAYOUT_SETTINGS },
			rateLimit: { ...DEFAULT_RATE_LIMIT_SETTINGS },
			profanityFilter: { ...DEFAULT_PROFANITY_FILTER_SETTINGS },
			responseLimit: { ...DEFAULT_RESPONSE_LIMIT_SETTINGS },
			passwordProtection: { ...DEFAULT_PASSWORD_PROTECTION_SETTINGS },
			notifications: { ...DEFAULT_NOTIFICATION_SETTINGS },
			duplicatePrevention: { ...DEFAULT_DUPLICATE_PREVENTION_SETTINGS },
			botProtection: { ...DEFAULT_BOT_PROTECTION_SETTINGS },
			metadata: { ...DEFAULT_METADATA_SETTINGS },
		},
	};
}
